import { useContext } from 'react'

import { IGetAllDashboardResponse } from 'services/GST/Dashboard'

import {
  DashboardContext,
  DashboardProvider,
  IPeriodo,
  TGraphicType,
  TContentReportType,
  ISelectedItensIds,
  TChecklistNames,
  TGridsNames
} from './provider'

interface IHandleChangeCheckAll {
  checkListName: TChecklistNames
  checked: boolean
}

interface THandleChangeCheckboxProps {
  itemId: number
  checkListName: TChecklistNames
}

interface IUseDashboard {
  periodo: IPeriodo
  setPeriodo: React.Dispatch<React.SetStateAction<IPeriodo>>
  contentReportType: TContentReportType
  setContentReportType: React.Dispatch<React.SetStateAction<TContentReportType>>
  reportType: TGraphicType
  setReportType: React.Dispatch<React.SetStateAction<TGraphicType>>
  selectedItensOfGrid: ISelectedItensIds
  setSelectedItensOfGrid: React.Dispatch<React.SetStateAction<ISelectedItensIds>>
  lastInteractionGridName: TGridsNames | null
  setLastInteractionGridName: React.Dispatch<React.SetStateAction<TGridsNames | null>>
  handleChangeCheckbox: (_props: THandleChangeCheckboxProps) => void
  unidadeGestoraId: number
  setUnidadeGestoraId: React.Dispatch<React.SetStateAction<number>>
  viewGraphic: boolean
  setViewGraphic: React.Dispatch<React.SetStateAction<boolean>>
  dashboardData: IGetAllDashboardResponse
  setDashboardData: React.Dispatch<React.SetStateAction<IGetAllDashboardResponse>>
  isCheckAll: (_checkListName: TChecklistNames) => boolean
  handleChangeCheckAll: (_props: IHandleChangeCheckAll) => void
}

const dashboardDataAlias = {
  idContraparte: 'listaContrapartes',
  idContraparteSubstituto: 'listaContrapartesSubstituto',
  idEmpresa: 'listaEmpresas',
  idCentroPlanta: 'listaCentros',
  idFornecedor: 'listaFornecedores',
}

const useDashboard = (): IUseDashboard => {
  const {
    viewGraphic,
    setViewGraphic,
    setPeriodo,
    periodo,
    contentReportType,
    setContentReportType,
    reportType,
    setReportType,
    selectedItensOfGrid,
    setSelectedItensOfGrid,
    lastInteractionGridName,
    setLastInteractionGridName,
    unidadeGestoraId,
    setUnidadeGestoraId,
    dashboardData,
    setDashboardData
  } = useContext(DashboardContext)

  const isCheckAll = (checklistName: TChecklistNames): boolean => {
    const dataListLength = dashboardData[dashboardDataAlias[checklistName]].length
    const selectedItensListLength = selectedItensOfGrid[checklistName].length

    return dataListLength > 0 && selectedItensListLength > 0 && dataListLength === selectedItensListLength
  }

  const handleChangeCheckbox = (props: THandleChangeCheckboxProps) => {
    const itemIsAlreadyChecked = (selectedItensOfGrid[props.checkListName].includes(props.itemId)) as boolean

    if (itemIsAlreadyChecked) {
      // Remover item da lista de checados.
      setSelectedItensOfGrid(prevState => ({
        ...prevState,
        [props.checkListName]: prevState[props.checkListName].filter(id => id !== props.itemId),
      }))
    } else {
      // Adicionar item na lista de checados.
      setSelectedItensOfGrid(prevState => ({
        ...prevState,
        [props.checkListName]: [...prevState[props.checkListName], props.itemId]
      }))
    }

    setLastInteractionGridName(dashboardDataAlias[props.checkListName] as TGridsNames)
  }

  const handleChangeCheckAll = (props: IHandleChangeCheckAll) => {
    const gridNameToCheckAll = dashboardDataAlias[props.checkListName]

    if (props.checked) {
      setSelectedItensOfGrid(prevState => ({
        ...prevState,
        [props.checkListName]: dashboardData[gridNameToCheckAll].map((gridItem: unknown) => gridItem['id']),
      }))
    } else {
      setSelectedItensOfGrid(prevState => ({
        ...prevState,
        [props.checkListName]: [],
      }))
    }

    setLastInteractionGridName(dashboardDataAlias[props.checkListName] as TGridsNames)
  }

  return {
    handleChangeCheckAll,
    isCheckAll,
    dashboardData,
    setDashboardData,
    viewGraphic,
    setViewGraphic,
    unidadeGestoraId,
    setUnidadeGestoraId,
    setSelectedItensOfGrid,
    handleChangeCheckbox,
    setLastInteractionGridName,
    lastInteractionGridName,
    selectedItensOfGrid,
    setPeriodo,
    periodo,
    contentReportType,
    setContentReportType,
    reportType,
    setReportType,
  }
}

export {
  DashboardProvider,
  useDashboard
}
